function hexToLinearRGB(hex: string): [number, number, number] {
  let n = hex.replace('#', '');
  if (n.length === 3) n = n.split('').map(c => c + c).join('');
  if (n.length === 8) n = n.slice(0, 6);
  const [r, g, b] = [0, 2, 4].map(i => parseInt(n.slice(i, i + 2), 16) / 255);
  const linearize = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return [linearize(r), linearize(g), linearize(b)];
}

// WCAG 2.x relative luminance. Picks #000 or #fff for text on the primary brand surface.
// Gamma decoding is load-bearing: a linear shortcut misclassifies vivid greens and cyans.
export function brandForeground(hex: string): '#000000' | '#ffffff' {
  const [r, g, b] = hexToLinearRGB(hex);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

// Tint percentage for --accent / --sidebar-accent, adapted to brand lightness so the
// tinted surface lands at oklab L ≈ 0.90 regardless of how dark or light the brand is.
// A fixed percentage renders black brands as dark gray (too dark) and light brands as
// pure white (no tint visible). Computed against oklab L (perceptual), not WCAG luminance
// (which weights green heavily and gives wrong numbers for chromatic brands).
// Clamped to [8%, 40%] so pure white brands don't push past a sane ceiling.
export function brandTintPercent(hex: string): string {
  const [r, g, b] = hexToLinearRGB(hex);
  const lms = [
    0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b,
    0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b,
    0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b,
  ].map(Math.cbrt) as [number, number, number];
  const L = 0.2104542553 * lms[0] + 0.7936177850 * lms[1] - 0.0040720468 * lms[2];
  const p = 0.1 / Math.max(1 - L, 0.0025);
  return `${Math.min(Math.max(p * 100, 8), 40).toFixed(1)}%`;
}
